import { FeatureToggle, isFeatureEnabled } from '../util/featureToggle';

export type TranslationType = Record<string, string>;

export default class TranslationManager {
    static #instance: TranslationManager | null = null;

    static #loading: boolean = false;

    static async getInstance(): Promise<TranslationManager> {
        if (this.#instance === null) {
            this.#instance = new TranslationManager();
        }

        if (Object.keys(this.#instance.translations).length === 0 && this.#loading === false) {
            try {
                await this.#instance.#getTranslationsFromJSON();
            } catch (error) {
                throw error;
            } finally {
                this.#loading = false;
            }
        }

        if (this.#loading === false) {
            return this.#instance;
        }

        await new Promise((resolve) => {
            const interval = setInterval(() => {
                if (this.#loading === false) {
                    clearInterval(interval);
                    resolve(true);
                }
            }, 250);
        });

        return this.#instance;
    }

    static destroyInstance(): void {
        this.#instance = null;
    }

    readonly #language = (): string => document.documentElement.dataset.language ?? 'en';

    #translations: TranslationType = {};

    get translations(): TranslationType {
        return this.#translations;
    }

    set translations(translations: TranslationType) {
        this.#translations = translations;
    }

    public translate(key: string, placeholders: Record<string, string> = {}): string {
        if (isFeatureEnabled(FeatureToggle.TRANSLATIONS)) {
            return `${key}:messages`;
        }

        const translation = this.#translations[key];
        if (!translation) {
            return key;
        }

        if (Object.keys(placeholders).length === 0) {
            return translation;
        }

        return this.#replacePlaceholders(translation, placeholders);
    }

    readonly #replacePlaceholders = (
        translation: string,
        placeholders: Record<string, string>
    ): string => {
        let translatedString = translation;

        for (const [varKey, varValue] of Object.entries(placeholders)) {
            translatedString = translatedString.replace(new RegExp(`{${varKey}}`, 'g'), varValue);
        }

        return translatedString;
    };

    async #getTranslationsFromJSON(): Promise<void> {
        try {
            const { translations } = await import(
                /* webpackChunkName: "async-messages-translations" */ `../../translations/${this.#language()}.json`
            );
            this.translations = translations;
        } catch (e: unknown) {
            const error = e as Error;
            error.message = `Importing translations from JSON: ${error.message}`;
            throw error;
        }
    }
}
